import { useState } from 'preact/hooks';
import { motion, AnimatePresence, useIsPresent } from 'framer-motion';
import { Sword, Shield, Footprints } from 'lucide-react';
import { TEXT_MUTED, DARK_CHIP_STYLE } from '../../lib/medievalTheme';
import { TOGGLEABLE_STATUS_EFFECTS as STATUS_EFFECTS } from '../../utils/game/cardMesh';
import { getEffectiveStatuses } from '../../utils/game/engine/automation';
import { countsAsUnit } from '../../utils/game/engine/core';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { Tooltip } from '../ui/tooltip';
import { getCanvasScale } from '../../utils/uiScale';
import { t } from '../../localization/t';
import { useEscDismiss } from './useEscDismiss';

// Shared texture URLs (mirrors medievalTheme.jsx. Kept private there).
// We compose them inline below so the badge + Clear surfaces pick up the
// same stone / chisel / scratches grit as the rest of the medieval UI.
const TEX_STONE     = 'url("/tex-stone.webp")';
const TEX_CHISEL    = 'url("/tex-chisel.webp")';
const TEX_SCRATCHES = 'url("/tex-scratches.webp")';

// Design-pixel constants. Multiplied by canvas scale at render time so the
// ring + badges scale with the rest of the UI. JS-driven layout means we
// can't use cqw directly in the position math.
const RING_RADIUS_DESIGN_PX = 115;
const BADGE_SIZE_DESIGN_PX = 38;

// Stat adjuster geometry (design pixels. Multiplied by canvasScale below).
const STAT_BTN_DESIGN_PX = 22;
const STAT_PILL_W_DESIGN_PX = 56;
const STAT_PILL_H_DESIGN_PX = 22;
const STAT_GAP_DESIGN_PX = 4;

// Central column layout (design px relative to ring center). Clear sits
// on top; movement / attack / defense stack beneath it. Each row is 22
// design-px tall, with ~6 design-px gaps so the whole column stays
// inside the safe area between the side badges (which reach the
// horizontal axis at ±ringRadius).
const CLEAR_ROW_OFFSET = -52;
const MOVEMENT_ROW_OFFSET = -8;
const ATTACK_ROW_OFFSET = 20;
const DEFENSE_ROW_OFFSET = 48;

const STAT_COLORS = {
  atk: '#f59e0b',
  hp: '#ef4444',
  mov: '#84cc16',
};

function badgeStyle(color, isActive, badgeSize) {
  // Layered material: stone + chisel + scratches textures over a
  // colored radial that catches a top-light. CRITICAL: there is a
  // solid dark `backgroundColor` under everything so the badge is
  // never see-through. Earlier the active gradient used low-alpha
  // suffixes (cc/88/33) which made the outer ring of activated
  // badges look transparent. The board showed straight through.
  // The colored radial now fades to `transparent` and falls onto
  // the solid `backgroundColor`, producing the same color-glow look
  // without any actual translucency.
  const colorRadial = isActive
    ? `radial-gradient(circle at 38% 30%, ${color} 0%, ${color}dd 40%, ${color}77 75%, transparent 100%)`
    : 'radial-gradient(circle at 38% 30%, #3a342c 0%, #1c1812 55%, #0a0806 100%)';
  return {
    width: badgeSize,
    height: badgeSize,
    borderRadius: '50%',
    backgroundColor: isActive ? '#0a0806' : '#0a0806',
    backgroundImage: `${TEX_STONE}, ${TEX_CHISEL}, ${TEX_SCRATCHES}, ${colorRadial}`,
    backgroundBlendMode: 'soft-light, overlay, soft-light, normal',
    border: `1px solid ${isActive ? 'rgba(232,200,120,0.95)' : 'rgba(180,140,60,0.55)'}`,
    color: isActive ? '#fff' : color,
    boxShadow: isActive
      ? [
          `0 0 0 1px ${color}aa`,
          `0 0 18px ${color}88`,
          `0 0 36px ${color}44`,
          'inset 0 1px 0 rgba(255,255,255,0.28)',
          'inset 0 -2px 6px rgba(0,0,0,0.45)',
          `inset 0 0 14px ${color}55`,
          '0 6px 14px rgba(0,0,0,0.65)',
        ].join(', ')
      : [
          'inset 0 1px 0 rgba(255,235,195,0.10)',
          'inset 0 -2px 6px rgba(0,0,0,0.45)',
          'inset 0 0 12px rgba(0,0,0,0.35)',
          '0 5px 12px rgba(0,0,0,0.62)',
        ].join(', '),
    fontSize: '0.5625cqw',
    fontWeight: 700,
    letterSpacing: '0.03em',
    textShadow: isActive
      ? '0 1px 3px rgba(0,0,0,0.85)'
      : `0 0 10px ${color}55, 0 1px 2px rgba(0,0,0,0.7)`,
  };
}

// Note on exits: we deliberately set NO `exit` prop on any motion
// component inside the ring menu. AnimatePresence unmounts children
// without an `exit` immediately. No fade, no shrink, and no chance
// for Framer's exit-complete callback to hang (which was the cause
// of the "menu freezes on status click and can't be closed" bug:
// any mismatch between an explicit exit target and the element's
// animated keys leaves the exit promise unresolved, AnimatePresence
// keeps the component mounted, `useIsPresent()` flips pointer-events
// to none, and the player is locked out). Entry animations stay
// (initial → animate); exit is just an unmount.

// Shared entry spring for every scale-in animation in the menu (ring,
// badges, central column). Damping is tuned just below critical for
// k=520 (≈45 vs. critical ~46) so the elements settle with a barely
// perceptible kiss-of-overshoot rather than the aggressive
// double-bounce the earlier 850/32 spring produced. Keep all entry
// animations on the same spring so nothing visually races ahead.
const ENTRY_SPRING = { type: 'spring', stiffness: 520, damping: 44, mass: 0.9 };

function StatAdjusterRow({
  cx,
  cy,
  rowOffset,
  canvasScale,
  Icon,
  iconColor,
  value,
  onMinus,
  onPlus,
  title,
}) {
  const btn = STAT_BTN_DESIGN_PX * canvasScale;
  const pillW = STAT_PILL_W_DESIGN_PX * canvasScale;
  const pillH = STAT_PILL_H_DESIGN_PX * canvasScale;
  const gap = STAT_GAP_DESIGN_PX * canvasScale;
  const totalW = btn + gap + pillW + gap + btn;
  const left = cx - totalW / 2;
  const top = cy + rowOffset * canvasScale - pillH / 2;

  const stepBtnStyle = {
    width: btn,
    height: btn,
    borderRadius: '50%',
    background: `${TEX_STONE}, ${TEX_SCRATCHES}, radial-gradient(circle at 38% 30%, #3a342c 0%, #1c1812 55%, #0a0806 100%)`,
    backgroundBlendMode: 'soft-light, overlay, normal',
    border: '1px solid rgba(180,140,60,0.6)',
    color: '#e8d5a0',
    fontWeight: 800,
    lineHeight: 1,
    fontSize: '0.7cqw',
    boxShadow: [
      'inset 0 1px 0 rgba(255,235,195,0.12)',
      'inset 0 -1px 2px rgba(0,0,0,0.4)',
      'inset 0 0 8px rgba(0,0,0,0.3)',
      '0 3px 7px rgba(0,0,0,0.6)',
    ].join(', '),
    textShadow: '0 1px 2px rgba(0,0,0,0.7)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  };

  return (
    <Tooltip content={title}>
      <motion.div
        className="absolute flex items-center"
        style={{ left, top, gap }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={ENTRY_SPRING}
      >
        <motion.button
          type="button"
          whileHover={{ scale: 1.12, transition: { duration: 0.08 } }}
          whileTap={{ scale: 0.92, transition: { duration: 0.05 } }}
          style={stepBtnStyle}
          onClick={(e) => {
            e.stopPropagation();
            playUI(UI.SELECT);
            onMinus();
          }}
        >−</motion.button>

        <div
          style={{
            ...DARK_CHIP_STYLE,
            width: pillW,
            height: pillH,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: gap,
            padding: `0 ${6 * canvasScale}px`,
            borderRadius: pillH / 2,
            color: '#e8d5a0',
            boxShadow: [
              'inset 0 0 0 1px rgba(255,235,195,0.07)',
              'inset 0 2px 4px rgba(0,0,0,0.55)',
              'inset 0 -1px 0 rgba(255,235,195,0.04)',
              '0 3px 9px rgba(0,0,0,0.6)',
            ].join(', '),
            textShadow: '0 1px 2px rgba(0,0,0,0.7)',
          }}
        >
          <Icon
            style={{ width: 14 * canvasScale, height: 14 * canvasScale }}
            strokeWidth={2.25}
            color={iconColor}
            aria-hidden="true"
          />
          <span
            style={{
              fontSize: '0.875cqw',
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              minWidth: 18 * canvasScale,
              textAlign: 'center',
            }}
          >
            {value}
          </span>
        </div>

        <motion.button
          type="button"
          whileHover={{ scale: 1.12, transition: { duration: 0.08 } }}
          whileTap={{ scale: 0.92, transition: { duration: 0.05 } }}
          style={stepBtnStyle}
          onClick={(e) => {
            e.stopPropagation();
            playUI(UI.SELECT);
            onPlus();
          }}
        >+</motion.button>
      </motion.div>
    </Tooltip>
  );
}

export default function StatusRingMenu({ ringMenu, onToggle, onClearAll, onAdjustStat, onClose, viewScale = 1 }) {
  const open = !!ringMenu;
  return (
    <AnimatePresence>
      {open && (
        <RingMenuContent
          ringMenu={ringMenu}
          onToggle={onToggle}
          onClearAll={onClearAll}
          onAdjustStat={onAdjustStat}
          onClose={onClose}
          viewScale={viewScale}
        />
      )}
    </AnimatePresence>
  );
}

function RingMenuContent({ ringMenu, onToggle, onClearAll, onAdjustStat, onClose, viewScale }) {
  const { cardInstance, x, y } = ringMenu;
  useEscDismiss(true, onClose);
  // Effective status union (v1.16a): manual + aura-granted + printed
  // keywords. The badge "lit" state covers all three so the ring
  // accurately reflects what the card currently has. The Clear button
  // only resets the manual statuses (clearAll() can't strip aura /
  // printed keywords. Those follow their source cards).
  const activeStatuses = getEffectiveStatuses(cardInstance);
  const manualStatuses = cardInstance.statuses || [];
  const canvasScale = getCanvasScale();
  const badgeSize = BADGE_SIZE_DESIGN_PX * canvasScale;
  const scale = viewScale || 1;
  const cx = x / scale;
  const cy = y / scale;
  const N = STATUS_EFFECTS.length;
  // Fit the ring radius to the badge count so neighbours always keep an
  // air gap. Adjacent badge centers sit a chord 2R·sin(π/N) apart; hold
  // that to at least one badge width plus a small gap. max() floors the
  // ring at its designed size so short lists don't shrink it. Growing
  // the list (recent status additions) now widens the ring instead of
  // overlapping the badges.
  const ringRadius = Math.max(
    RING_RADIUS_DESIGN_PX * canvasScale,
    (badgeSize + 10 * canvasScale) / (2 * Math.sin(Math.PI / N)),
  );

  // `cardInstance.currentAttack`/`currentLife` mutate in place inside
  // GameBoard.applyLifeButton; this local tick is what forces React to
  // re-read those mutated fields after each step.
  const [, setTick] = useState(0);
  const bumpRefresh = () => setTick((t) => t + 1);

  // While AnimatePresence is playing our exit animation we're still in
  // the DOM. Without this, the fullscreen backdrop keeps pointer-events:
  // auto and swallows the next shift-click on a card. The canvas
  // mousedown never fires, so the ring fails to reopen until the user
  // clicks again.
  const isPresent = useIsPresent();

  // Minions show both ATK + DEF rows; avatars carry only `currentAttack`
  // and surface just the attack row (their hit points live on the
  // player-side life tracker, not on the card).
  const hasAttack = cardInstance.currentAttack !== undefined && onAdjustStat;
  const hasDefense = cardInstance.currentLife !== undefined && onAdjustStat;
  // v1.91. Movement row. Available for any card the engine treats as
  // a movable unit (Minions + Avatars). Sites + Magic spells / Auras
  // don't move, so we skip the row for them.
  const isMovableUnit = countsAsUnit(cardInstance);
  const hasMovement = isMovableUnit && onAdjustStat;
  const hasStats = hasAttack || hasDefense || hasMovement;

  return (
    <motion.div
      className="fixed inset-0 z-[1100]"
      style={{ zoom: scale, pointerEvents: isPresent ? 'auto' : 'none' }}
      onClick={(e) => { e.stopPropagation(); playUI(UI.CANCEL); onClose(); }}
      // Right-click outside the ring also dismisses it. Without this
      // the backdrop silently absorbs every contextmenu event while
      // the ring is open. The player sees right-click "stop working"
      // on cards until they happen to left-click the backdrop first.
      onContextMenu={(e) => { e.stopPropagation(); playUI(UI.CANCEL); onClose(); }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // Instant exit. No fade. Clicking a badge applies the status
      // and dismisses the menu; the just-pressed button must never
      // appear to ghost out. Entry fade-in stays for the soft open.
      transition={{ duration: 0.05 }}
    >
      {(() => {
        // Radial vignette focused on the ring's centre. The flat 30%
        // overlay we used before kept the entire board readable
        // through the ring, which made it ambiguous which card the
        // open ring belonged to. Fading the darkness past the outer
        // badge edge (~134px design) so the menu reads as floating
        // above the table; same idea as ScryDialog's strip vignette.
        const shadowRadius = ringRadius + (BADGE_SIZE_DESIGN_PX + 56) * canvasScale;
        return (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle ${shadowRadius}px at ${cx}px ${cy}px, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.74) 32%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.22) 84%, transparent 100%)`,
            }}
          />
        );
      })()}

      {/* Connector ring. Plain CSS circle passing through every
          badge center. Pure `style.left/top/width/height` (no animated
          scale, no SVG box math), so it is mathematically impossible
          for the ring to drift relative to the badges: the ring's
          radius and the badge ring radius read from the same value.
          The double-border bevel is faked via inset / outer box-shadows. */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: cx - ringRadius,
          top: cy - ringRadius,
          width: ringRadius * 2,
          height: ringRadius * 2,
          borderRadius: '50%',
          border: '1px solid rgba(180,140,60,0.55)',
          boxShadow: [
            'inset 0 0 0 1px rgba(255,235,195,0.14)',
            `0 0 ${24 * canvasScale}px rgba(180,140,60,0.16)`,
            '0 2px 8px rgba(0,0,0,0.5)',
          ].join(', '),
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.12 }}
        aria-hidden="true"
      />
      {STATUS_EFFECTS.map((effect, i) => {
        // Lance lights from EQUIPMENT, not statuses. The combat
        // engine reads Lance off equippedCards, and the ring toggle
        // routes to equip/unequip (see equipActions.toggleLanceEquip).
        const isActive = effect.key === 'lance'
          ? (cardInstance.equippedCards || []).some((c) => (c?.name || '').toLowerCase() === 'lance')
          : activeStatuses.includes(effect.key);
        const angle = ((2 * Math.PI) / N) * i - Math.PI / 2;
        const tx = cx + ringRadius * Math.cos(angle) - badgeSize / 2;
        const ty = cy + ringRadius * Math.sin(angle) - badgeSize / 2;

        // Position lives in `style`. Never in motion `animate`.
        // so the badge is glued to its ring slot regardless of what
        // Framer's animation state is doing. Only `scale` animates,
        // which uses transforms (centered) and can't shift the badge
        // off the ring. Exit is instant: no fade, no shrink. The
        // press must never appear to ghost out. No `exit` prop means
        // AnimatePresence unmounts the badge instantly the moment
        // `open` flips false, so the click reads as "snap, done".
        return (
          <Tooltip key={effect.key} content={effect.label}>
            <motion.button
              type="button"
              className="absolute flex items-center justify-center cursor-pointer"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
                    transition={ENTRY_SPRING}
              whileHover={{ scale: 1.18, transition: { duration: 0.08 } }}
              whileTap={{ scale: 0.92, transition: { duration: 0.05 } }}
              style={{ ...badgeStyle(effect.color, isActive, badgeSize), left: tx, top: ty }}
              onClick={(e) => {
                e.stopPropagation();
                playUI(UI.SELECT);
                onToggle(cardInstance, effect.key);
                onClose();
              }}
            >
              {effect.abbr}
            </motion.button>
          </Tooltip>
        );
      })}

      {/* Center column. Clear button at top, then movement / attack
          / defense stacked beneath it. The Clear button only renders
          when at least one MANUAL status exists; aura-granted and
          printed keywords belong to their source and can't be cleared
          from this UI. */}
      {manualStatuses.length > 0 ? (
        <Tooltip content={t('Clear All')}>
        <motion.button
          type="button"
          className="absolute flex items-center justify-center cursor-pointer"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
            transition={ENTRY_SPRING}
          whileHover={{ scale: 1.12, transition: { duration: 0.08 } }}
          whileTap={{ scale: 0.94, transition: { duration: 0.05 } }}
          style={{
            left: cx - 22 * canvasScale,
            top: cy + CLEAR_ROW_OFFSET * canvasScale - 22 * canvasScale,
            width: 44 * canvasScale,
            height: 44 * canvasScale,
            borderRadius: '50%',
            background: `${TEX_STONE}, ${TEX_CHISEL}, ${TEX_SCRATCHES}, radial-gradient(circle at 38% 30%, #7a2a2a 0%, #4a1818 50%, #220a0a 100%)`,
            backgroundBlendMode: 'soft-light, overlay, soft-light, normal',
            border: '1px solid rgba(220,90,90,0.85)',
            color: '#ffd0c8',
            boxShadow: [
              '0 0 0 1px rgba(120,30,30,0.7)',
              '0 0 18px rgba(200,60,60,0.45)',
              'inset 0 1px 0 rgba(255,220,200,0.3)',
              'inset 0 -2px 6px rgba(0,0,0,0.5)',
              'inset 0 0 14px rgba(160,30,30,0.55)',
              '0 6px 14px rgba(0,0,0,0.65)',
            ].join(', '),
            fontSize: '0.5cqw',
            fontWeight: 800,
            letterSpacing: '0.06em',
            textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 0 8px rgba(255,120,100,0.35)',
          }}
          onClick={(e) => {
            e.stopPropagation();
            playUI(UI.CANCEL);
            onClearAll(cardInstance);
            onClose();
          }}
        >
          {t('Clear')}
        </motion.button>
        </Tooltip>
      ) : (
        <motion.div
          className="absolute pointer-events-none text-[0.625cqw] font-semibold text-center"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
            transition={ENTRY_SPRING}
          style={{
            left: cx - 40 * canvasScale,
            top: cy + CLEAR_ROW_OFFSET * canvasScale - 6 * canvasScale,
            width: 80 * canvasScale,
            color: TEXT_MUTED,
            textShadow: '0 1px 4px rgba(0,0,0,0.8)',
          }}
        >
          {t('Status')}
        </motion.div>
      )}

      {hasStats && (
        <>
          {hasMovement && (
            <StatAdjusterRow
              cx={cx}
              cy={cy}
              rowOffset={MOVEMENT_ROW_OFFSET}
              canvasScale={canvasScale}
              Icon={Footprints}
              iconColor={STAT_COLORS.mov}
              value={1 + (cardInstance._movementBonus || 0)}
              title={t('Movement')}
              onMinus={() => { onAdjustStat(cardInstance, 'mov', 'decrement'); bumpRefresh(); }}
              onPlus={() => { onAdjustStat(cardInstance, 'mov', 'increment'); bumpRefresh(); }}
            />
          )}
          {hasAttack && (
            <StatAdjusterRow
              cx={cx}
              cy={cy}
              rowOffset={ATTACK_ROW_OFFSET}
              canvasScale={canvasScale}
              Icon={Sword}
              iconColor={STAT_COLORS.atk}
              value={cardInstance.currentAttack ?? 0}
              title={t('Attack')}
              onMinus={() => { onAdjustStat(cardInstance, 'atk', 'decrement'); bumpRefresh(); }}
              onPlus={() => { onAdjustStat(cardInstance, 'atk', 'increment'); bumpRefresh(); }}
            />
          )}
          {hasDefense && (
            <StatAdjusterRow
              cx={cx}
              cy={cy}
              rowOffset={DEFENSE_ROW_OFFSET}
              canvasScale={canvasScale}
              Icon={Shield}
              iconColor={STAT_COLORS.hp}
              value={cardInstance.currentLife ?? 0}
              title={t('Defense')}
              onMinus={() => { onAdjustStat(cardInstance, 'hp', 'decrement'); bumpRefresh(); }}
              onPlus={() => { onAdjustStat(cardInstance, 'hp', 'increment'); bumpRefresh(); }}
            />
          )}
        </>
      )}
    </motion.div>
  );
}
